import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { ConfigProvider, DatePicker, Input, Popover } from 'antd';
import { DownOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';
import useAxiosClient from '../axios-client.js';
import { useStateContext } from '../contexts/ContextProvider.jsx';
import { useFabricBalances } from '../hooks/useFabricBalances.jsx';
import { statusMeta } from '../components/Order/orderPrimitives.jsx';
import { coverage, shelfLabel, sortRequests, withOnHand } from '../components/Stock/fabricRequests';
import { LOW_STOCK_KG } from '../components/Stock/fabricShelves';
import {
  FilterChip, LoadingRow, MenuList, Pagination, RowsPicker, PanelDrawer, StatTile,
  filterBtn, miniLabel,
} from '../components/Stock/stockChrome.jsx';

import { PRIMARY, shellAntdTheme, shellVars, STATUS } from '../shellTokens';

const { RangePicker } = DatePicker;

const PAGE_SIZE_OPTIONS = [15, 30, 50];

/**
 * Named ranges, over the *delivery* date — what the store is being asked for
 * and by when, not when the order was taken.
 *
 * "Recent and upcoming" is the default and is deliberately not "everything":
 * the order book still holds committed orders with delivery dates going back to
 * 2023 that nobody ever closed, and they outnumber the live ones. An
 * unqualified total would be dominated by work that is not coming.
 */
const PRESETS = [
  { key: 'recent', label: 'Recent & upcoming', range: () => [dayjs().subtract(30, 'day'), null] },
  { key: 'next30', label: 'Due in 30 days', range: () => [dayjs(), dayjs().add(30, 'day')] },
  { key: 'thisMonth', label: 'This month', range: () => [dayjs().startOf('month'), dayjs().endOf('month')] },
  { key: 'all', label: 'Every open order', range: () => null },
];

const DEFAULT_PRESET = 'recent';

/**
 * The status sets worth asking for, as sets rather than as checkboxes.
 *
 * Approved → Print → Production is one span in practice — cloth is wanted
 * across all three — so the default is the span, and the single-status entries
 * are for the floor asking "what is on the print table". Held orders are last
 * and separate: a hold is a pause, and the store should not be cutting for one
 * without having asked to see it.
 */
const STATUS_SETS = [
  { key: 'awaiting', label: 'Approved → Production', statuses: [2, 12, 3] },
  { key: 'approved', label: 'Approved only', statuses: [2] },
  { key: 'print', label: 'On the print floor', statuses: [12] },
  { key: 'production', label: 'In production', statuses: [3] },
  { key: 'held', label: 'On hold', statuses: [11] },
  { key: 'withHeld', label: 'Awaiting, held included', statuses: [2, 12, 3, 11] },
];

const SORT_LABELS = {
  pieces: 'Most wanted',
  urgency: 'Soonest due',
  stock: 'Thinnest shelf',
};

const fmtKg = (n) => Number(n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtNum = (n) => Number(n ?? 0).toLocaleString();

const useIsMobile = () => {
  const [mobile, setMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return mobile;
};

/** Fluid columns with a floor, the shape the other fabric tables use. */
const FABRIC_GRID = [
  'minmax(0,2.6fr)',     // fabric
  'minmax(74px,1fr)',    // pieces
  'minmax(64px,0.8fr)',  // orders
  'minmax(88px,1.1fr)',  // earliest delivery
  'minmax(96px,1.3fr)',  // on hand
].join(' ');

const ORDER_GRID = [
  'minmax(0,1.8fr)',     // order
  'minmax(84px,1fr)',    // status
  'minmax(74px,0.9fr)',  // delivery
  'minmax(0,2.2fr)',     // fabric
  'minmax(58px,0.7fr)',  // pieces
  'minmax(96px,1.2fr)',  // on hand
].join(' ');

const numCell = {
  padding: '11px 8px', textAlign: 'right', font: '600 12.5px monospace',
  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
};

const clip = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };

/**
 * What the store holds against one demand row, said in the store's own terms.
 *
 * Never against the request: pieces and kilograms are not comparable here, so
 * this reports the shelf and stops.
 */
function OnHandCell({ row, align = 'right' }) {
  const state = coverage(row);

  if (state === 'unknown') {
    return <span style={{ font: '400 11.5px Outfit, sans-serif', color: 'var(--dim)' }}>—</span>;
  }

  const tint = state === 'empty' ? STATUS.errorSoft : state === 'low' ? STATUS.warn : 'var(--muted)';
  // Without a print the figure is the cloth across every print it is carried
  // in, and no single order line can draw on all of them — so say which it is.
  const spread = !row.held.exact && row.held.prints > 1 ? ` /${row.held.prints}` : '';

  return (
    <span
      style={{ font: '600 12.5px monospace', color: tint, textAlign: align }}
      title={[
        state === 'empty' ? 'nothing on hand in the store' : `${fmtKg(row.held.weight)} kg · ${row.held.rolls} rolls on hand`,
        state === 'low' ? `below ${LOW_STOCK_KG} kg — running low` : null,
        spread ? `summed across ${row.held.prints} prints` : null,
      ].filter(Boolean).join(' — ')}
    >
      {state === 'empty' ? 'none' : `${fmtKg(row.held.weight)} kg${spread}`}
    </span>
  );
}

/**
 * An order, named and linked exactly as the Orders list names and links it:
 * `#<id>`, to /orders/<id>.
 *
 * The numeric id, not the stored `order_id`. `ET-ORD-<datetime><seq>` is 24
 * characters of which the leading 18 are identical on every row, and no screen
 * in the app shows it — so printing it here would give one order two names
 * depending on where you met it.
 *
 * stopPropagation because these sit inside rows that open the shelf drawer:
 * following the order and inspecting the cloth are different intents.
 */
function OrderLink({ orderId }) {
  return (
    <Link
      to={`/orders/${orderId}`}
      title="Open full details page"
      onClick={(e) => e.stopPropagation()}
      style={{ font: '600 12.5px monospace', color: 'var(--activeText)', ...clip }}
    >
      #{orderId}
    </Link>
  );
}

function StatusChip({ status, heldFrom, darkMode }) {
  const meta = statusMeta(status, darkMode);
  // A hold says nothing on its own — what the store needs is the stage it was
  // held at, because that is the stage it will go back to.
  const held = status === 11 && heldFrom ? statusMeta(heldFrom, darkMode) : null;

  return (
    <span
      title={held ? `Held from ${held.label}` : undefined}
      style={{
        font: '700 10.5px Outfit, sans-serif', letterSpacing: '.04em',
        color: meta.color, background: `${meta.color}29`,
        padding: '3px 7px', borderRadius: 7, ...clip, display: 'inline-block', maxWidth: '100%',
      }}
    >
      {held ? `HELD · ${held.label}` : meta.label}
    </span>
  );
}

function EmptyState({ note }) {
  return (
    <div style={{ padding: '46px 18px', textAlign: 'center' }}>
      <div style={{ font: '600 13.5px Outfit, sans-serif', color: 'var(--muted)' }}>Nothing asked for</div>
      <div style={{ font: '400 12px Outfit, sans-serif', color: 'var(--dim)', marginTop: 4 }}>
        {note || 'No open order in this range needs cloth from the store.'}
      </div>
    </div>
  );
}

/**
 * What the order book is asking the fabric store for.
 *
 * An order product line already names a shelf — fabric, colour and print are
 * the same triple the store keys its balances on — so this is the join nobody
 * had made: every order that is committed but not yet finished, rolled up into
 * the cloth it needs, set beside what is actually on the shelf.
 *
 * Two things it does not claim, and both are load-bearing:
 *
 *   - **It cannot tell you whether the stock covers the orders.** Nothing
 *     records how much cloth a piece takes. Pieces and kilograms sit side by
 *     side and the person who knows the cut does the arithmetic.
 *   - **It is not a commitment.** Nothing here reserves, allocates or moves
 *     fabric; issuing to production is still a PRODUCTION outflow on the Fabric
 *     Store screen. This is a read, and it goes stale the moment an order moves.
 */
function FabricRequests() {
  const axiosClient = useAxiosClient();
  const { darkMode } = useStateContext();
  const isMobile = useIsMobile();
  const { onHand, known } = useFabricBalances();

  const [view, setView] = useState('fabric'); // 'fabric' | 'order'
  const [fabricRows, setFabricRows] = useState([]);
  const [orderRows, setOrderRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [sort, setSort] = useState('pieces');

  const [preset, setPreset] = useState(DEFAULT_PRESET);
  const [range, setRange] = useState(() => PRESETS[0].range());
  const [statusSet, setStatusSet] = useState('awaiting');
  const [orderIdInput, setOrderIdInput] = useState('');
  const [orderId, setOrderId] = useState('');

  const [rangeMenuOpen, setRangeMenuOpen] = useState(false);
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);
  const [sortMenuOpen, setSortMenuOpen] = useState(false);

  // One shelf, and the orders behind it.
  const [detail, setDetail] = useState(null);
  const [detailRows, setDetailRows] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);

  /**
   * The filters as the endpoint takes them, derived from state rather than
   * passed around — so the fetch below has exactly one trigger and a filter
   * change cannot fire two requests that disagree about what is being asked.
   */
  const params = useMemo(() => {
    const statuses = (STATUS_SETS.find((s) => s.key === statusSet) || STATUS_SETS[0]).statuses;

    return {
      from: range?.[0] ? range[0].format('YYYY-MM-DD') : undefined,
      to: range?.[1] ? range[1].format('YYYY-MM-DD') : undefined,
      statuses: statuses.join(','),
      orderId: orderId || undefined,
    };
  }, [range, statusSet, orderId]);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      if (view === 'fabric') {
        // The whole rollup in one read — under a hundred rows, and the sorting
        // and the shelf lookups it feeds are all client-side from here.
        const res = await axiosClient.get('/fabricStock/requests/byFabric', { params });
        setFabricRows(res.data?.data || []);
        setSummary(res.data?.summary || null);
      } else {
        const res = await axiosClient.get('/fabricStock/requests', {
          params: { ...params, page, perPage: pageSize },
        });
        setOrderRows(res.data?.data || []);
        setSummary(res.data?.summary || null);
        setTotal(res.data?.meta?.total || 0);
      }
    } catch {
      toast.error('Could not load what the orders are asking for.');
    } finally {
      setLoading(false);
    }
  }, [axiosClient, params, page, pageSize, view]);

  // The one place a request is made: every control below simply moves state.
  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const applyPreset = (key) => {
    // "Custom" only reveals the picker — it names how the range is being set,
    // not a range of its own, so the one on screen stands until it changes.
    if (key === 'custom') { setPreset('custom'); return; }
    setPreset(key);
    setRange((PRESETS.find((p) => p.key === key) || PRESETS[0]).range());
    setPage(1);
  };

  const applyRange = (value) => {
    setPreset('custom');
    setRange(value);
    setPage(1);
  };

  const applyStatusSet = (key) => { setStatusSet(key); setPage(1); };

  const applyOrderId = (value) => { setOrderId(value); setPage(1); };

  const switchView = (next) => {
    if (next === view) return;
    setView(next);
    setPage(1);
  };

  // The orders behind one shelf. Asked for on open rather than held for every
  // row: 82 shelves' worth of order lists is the whole table again.
  useEffect(() => {
    if (!detail) { setDetailRows([]); return; }
    let live = true;
    setDetailLoading(true);
    axiosClient.get('/fabricStock/requests', {
      params: {
        ...params,
        fabricTypeId: detail.fabricTypeId,
        fabricColorId: detail.fabricColorId,
        fabricPatternId: detail.fabricPatternId ?? 'none',
        perPage: 100,
        page: 1,
      },
    })
      .then((res) => { if (live) setDetailRows(res.data?.data || []); })
      .catch(() => { if (live) setDetailRows([]); })
      .finally(() => { if (live) setDetailLoading(false); });
    return () => { live = false; };
  }, [detail, axiosClient, params]);

  // Balances arrive on their own schedule, so this re-runs when they land.
  const shelves = useMemo(
    () => sortRequests(withOnHand(fabricRows, known ? onHand : null), sort),
    [fabricRows, onHand, known, sort],
  );

  const lines = useMemo(() => withOnHand(orderRows, known ? onHand : null), [orderRows, onHand, known]);

  const rootStyle = shellVars(darkMode);
  const lastPage = Math.max(1, Math.ceil(total / pageSize));
  const rangeLabel = preset === 'all'
    ? 'every open order'
    : range?.[0]
      ? `${range[0].format('D MMM YY')} → ${range[1] ? range[1].format('D MMM YY') : 'onwards'}`
      : 'every open order';
  const presetLabel = preset === 'custom' ? 'Custom' : (PRESETS.find((p) => p.key === preset)?.label || 'Range');
  const statusLabel = STATUS_SETS.find((s) => s.key === statusSet)?.label || 'Statuses';
  const bare = shelves.filter((r) => coverage(r) === 'empty').length;

  return (
    <div className="et-req" style={rootStyle}>
      <style>{`
        .et-req{font-family:Outfit,system-ui,sans-serif;color:var(--text);display:flex;flex-direction:column;gap:14px;height:100%;min-height:0}
        .et-req__row:hover{background:var(--hover)}
        .et-req__table{flex:1;min-height:0;display:flex;flex-direction:column;background:var(--surface);border:1px solid var(--border);border-radius:14px;overflow:hidden}
        .et-req__seg{display:flex;background:var(--surface2);border:1px solid var(--border);border-radius:8px;padding:2px;gap:2px}
        .et-req__seg button{height:26px;padding:0 11px;border:0;border-radius:6px;background:transparent;color:var(--muted);font:600 12px Outfit,sans-serif;cursor:pointer}
        .et-req__seg button[data-on="1"]{background:var(--surface);color:var(--text);box-shadow:0 1px 2px rgba(0,0,0,.08)}
      `}</style>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap' }}>
        <div>
          <div style={{ font: '700 22px Outfit, sans-serif', letterSpacing: '-.01em' }}>Fabric Requests</div>
          <div style={{ font: '400 13px Outfit, sans-serif', color: 'var(--muted)', marginTop: 2 }}>
            What the open orders need from the store · {rangeLabel}
          </div>
        </div>
      </div>

      <ConfigProvider theme={shellAntdTheme(darkMode)}>
        {summary && (
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)', gap: 14 }}>
            <StatTile
              icon="▤" tint={PRIMARY} label="Pieces wanted"
              value={fmtNum(summary.pieces)}
              foot={`${fmtNum(summary.lines)} order line${summary.lines === 1 ? '' : 's'}`}
            />
            <StatTile
              icon="◷" tint="#a78bfa" label="Orders waiting"
              value={fmtNum(summary.orders)}
              foot={statusLabel.toLowerCase()}
            />
            <StatTile
              icon="▦" tint={bare > 0 ? STATUS.errorSoft : STATUS.ok} label="Fabrics wanted"
              value={fmtNum(summary.shelves)}
              foot={bare > 0 ? `${bare} with an empty shelf` : known ? 'all stocked' : 'balances loading'}
            />
            {/* Past its delivery date and still asking for cloth — the rows
                worth reading before any of the others. */}
            <StatTile
              icon="!" tint={summary.overdueOrders > 0 ? STATUS.errorSoft : STATUS.ok} label="Overdue"
              value={fmtNum(summary.overdueOrders)}
              foot={summary.orders > 0 && summary.overdueOrders > 0
                ? `${Math.round((summary.overdueOrders / summary.orders) * 100)}% of orders waiting`
                : 'all within date'}
            />
          </div>
        )}

        <div className="et-req__table">
          {/* Toolbar */}
          <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap' }}>
            <div className="et-req__seg">
              <button type="button" data-on={view === 'fabric' ? '1' : '0'} onClick={() => switchView('fabric')}>By fabric</button>
              <button type="button" data-on={view === 'order' ? '1' : '0'} onClick={() => switchView('order')}>By order</button>
            </div>

            <div className="et-toolbar">
              <Popover
                trigger="click"
                open={rangeMenuOpen}
                onOpenChange={setRangeMenuOpen}
                content={
                  <MenuList
                    darkMode={darkMode}
                    items={[...PRESETS.map((p) => ({ key: p.key, label: p.label })), { key: 'custom', label: 'Custom range…' }]}
                    active={preset}
                    onSelect={(key) => { setRangeMenuOpen(false); applyPreset(key); }}
                  />
                }
              >
                <button type="button" style={filterBtn}>
                  {presetLabel} <DownOutlined style={{ fontSize: 9, color: 'var(--muted)' }} />
                </button>
              </Popover>

              {preset === 'custom' && (
                <RangePicker
                  value={range}
                  onChange={applyRange}
                  format="D MMM YY"
                  style={{ width: isMobile ? '100%' : 230 }}
                />
              )}

              <Popover
                trigger="click"
                open={statusMenuOpen}
                onOpenChange={setStatusMenuOpen}
                content={
                  <MenuList
                    darkMode={darkMode}
                    items={STATUS_SETS.map((s) => ({ key: s.key, label: s.label }))}
                    active={statusSet}
                    onSelect={(key) => { setStatusMenuOpen(false); applyStatusSet(key); }}
                  />
                }
              >
                <button type="button" style={filterBtn}>
                  {statusLabel} <DownOutlined style={{ fontSize: 9, color: 'var(--muted)' }} />
                </button>
              </Popover>

              {view === 'fabric' ? (
                <Popover
                  trigger="click"
                  open={sortMenuOpen}
                  onOpenChange={setSortMenuOpen}
                  content={
                    <MenuList
                      darkMode={darkMode}
                      items={Object.entries(SORT_LABELS).map(([key, label]) => ({ key, label }))}
                      active={sort}
                      onSelect={(key) => { setSortMenuOpen(false); setSort(key); }}
                    />
                  }
                >
                  <button type="button" style={filterBtn}>
                    {SORT_LABELS[sort]} <DownOutlined style={{ fontSize: 9, color: 'var(--muted)' }} />
                  </button>
                </Popover>
              ) : (
                <Input
                  allowClear
                  placeholder="Order ID"
                  prefix={<SearchOutlined style={{ color: 'var(--dim)' }} />}
                  style={{ width: isMobile ? '100%' : 180 }}
                  value={orderIdInput}
                  onChange={(e) => {
                    setOrderIdInput(e.target.value);
                    if (e.target.value === '' && orderId !== '') applyOrderId('');
                  }}
                  onPressEnter={() => applyOrderId(orderIdInput)}
                />
              )}
            </div>
          </div>

          {/* Filter chips — one place that states what is being left out. */}
          {(preset !== DEFAULT_PRESET || statusSet !== 'awaiting' || orderId) && (
            <div style={{ padding: '10px 18px', borderBottom: '1px solid var(--border)', background: 'var(--bg)', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ ...miniLabel, marginRight: 2 }}>Filters</span>
              {preset !== DEFAULT_PRESET && (
                <FilterChip label="Due" value={rangeLabel} onClear={() => applyPreset(DEFAULT_PRESET)} />
              )}
              {statusSet !== 'awaiting' && (
                <FilterChip label="Status" value={statusLabel} onClear={() => applyStatusSet('awaiting')} />
              )}
              {orderId && (
                <FilterChip label="Order" value={`#${orderId}`} onClear={() => { setOrderIdInput(''); applyOrderId(''); }} />
              )}
            </div>
          )}

          {/* By fabric */}
          {view === 'fabric' && !isMobile && (
            <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
              <div style={{ display: 'grid', gridTemplateColumns: FABRIC_GRID, background: 'var(--surface2)', borderBottom: '1px solid var(--border)', position: 'sticky', top: 0, zIndex: 1 }}>
                {[['Fabric', 'left'], ['Pieces', 'right'], ['Orders', 'right'], ['Earliest due', 'right'], ['On hand', 'right']].map(([h, align], i) => (
                  <div key={h} style={{
                    padding: i === 0 ? '9px 12px' : '9px 8px', display: 'flex', alignItems: 'center',
                    justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
                    font: '700 11.5px Outfit, sans-serif', color: 'var(--muted)',
                    letterSpacing: '.05em', textTransform: 'uppercase', ...clip,
                  }}>{h}</div>
                ))}
              </div>

              {loading && <LoadingRow />}
              {!loading && shelves.length === 0 && <EmptyState />}

              {!loading && shelves.map((r) => (
                <div
                  key={`${r.fabricTypeId}:${r.fabricColorId}:${r.fabricPatternId ?? 0}`}
                  className="et-req__row"
                  role="button"
                  tabIndex={0}
                  onClick={() => setDetail(r)}
                  onKeyDown={(e) => { if (e.key === 'Enter') setDetail(r); }}
                  style={{ display: 'grid', gridTemplateColumns: FABRIC_GRID, borderBottom: '1px solid var(--border)', alignItems: 'center', cursor: 'pointer' }}
                >
                  <div style={{ padding: '11px 12px', minWidth: 0 }}>
                    <div style={{ font: '600 12.5px Outfit, sans-serif', color: 'var(--text)', ...clip }}>
                      {shelfLabel(r)}
                    </div>
                    <div style={{ font: '400 11px Outfit, sans-serif', color: 'var(--dim)', ...clip }}>
                      {r.lines} line{r.lines === 1 ? '' : 's'}
                      {r.overdueOrders > 0 && <> · <span style={{ color: STATUS.errorSoft }}>{r.overdueOrders} overdue</span></>}
                    </div>
                  </div>
                  <div style={{ ...numCell, fontWeight: 700 }}>{fmtNum(r.pieces)}</div>
                  <div style={{ ...numCell, color: 'var(--muted)' }}>{fmtNum(r.orders)}</div>
                  <div style={{ ...numCell, fontWeight: 400, color: r.overdueOrders > 0 ? STATUS.errorSoft : 'var(--muted)' }}>
                    {r.earliestDelivery ? dayjs(r.earliestDelivery).format('D MMM YY') : '—'}
                  </div>
                  <div style={{ ...numCell, fontWeight: 400 }}><OnHandCell row={r} /></div>
                </div>
              ))}
            </div>
          )}

          {/* By order */}
          {view === 'order' && !isMobile && (
            <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
              <div style={{ display: 'grid', gridTemplateColumns: ORDER_GRID, background: 'var(--surface2)', borderBottom: '1px solid var(--border)', position: 'sticky', top: 0, zIndex: 1 }}>
                {[['Order', 'left'], ['Status', 'left'], ['Due', 'left'], ['Fabric', 'left'], ['Pieces', 'right'], ['On hand', 'right']].map(([h, align], i) => (
                  <div key={h} style={{
                    padding: i === 0 ? '9px 12px' : '9px 8px', display: 'flex', alignItems: 'center',
                    justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
                    font: '700 11.5px Outfit, sans-serif', color: 'var(--muted)',
                    letterSpacing: '.05em', textTransform: 'uppercase', ...clip,
                  }}>{h}</div>
                ))}
              </div>

              {loading && <LoadingRow />}
              {!loading && lines.length === 0 && <EmptyState />}

              {!loading && lines.map((r) => (
                <div key={r.lineId} style={{ display: 'grid', gridTemplateColumns: ORDER_GRID, borderBottom: '1px solid var(--border)', alignItems: 'center' }} className="et-req__row">
                  <div style={{ padding: '11px 12px', minWidth: 0 }}>
                    <OrderLink orderId={r.orderId} />
                  </div>
                  <div style={{ padding: '11px 8px', minWidth: 0 }}>
                    <StatusChip status={r.status} heldFrom={r.heldFromStatus} darkMode={darkMode} />
                  </div>
                  <div style={{ padding: '11px 8px', font: '400 12px monospace', color: r.overdue ? STATUS.errorSoft : 'var(--muted)', ...clip }}>
                    {r.deliveryDate ? dayjs(r.deliveryDate).format('D MMM YY') : '—'}
                  </div>
                  <div style={{ padding: '11px 8px', font: '400 12.5px Outfit, sans-serif', color: 'var(--text)', ...clip }}>
                    {shelfLabel(r)}
                  </div>
                  <div style={{ ...numCell, fontWeight: 700 }}>{fmtNum(r.pieces)}</div>
                  <div style={{ ...numCell, fontWeight: 400 }}><OnHandCell row={r} /></div>
                </div>
              ))}
            </div>
          )}

          {/* Mobile cards — one shape for both views. */}
          {isMobile && (
            <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
              {loading && <LoadingRow />}
              {!loading && view === 'fabric' && shelves.length === 0 && <EmptyState />}
              {!loading && view === 'order' && lines.length === 0 && <EmptyState />}

              {!loading && view === 'fabric' && shelves.map((r) => (
                <div
                  key={`${r.fabricTypeId}:${r.fabricColorId}:${r.fabricPatternId ?? 0}`}
                  onClick={() => setDetail(r)}
                  style={{ border: '1px solid var(--border)', borderRadius: 12, padding: 12, background: 'var(--surface2)' }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ font: '600 13.5px Outfit, sans-serif', ...clip }}>{shelfLabel(r)}</div>
                      <div style={{ font: '400 11.5px Outfit, sans-serif', color: 'var(--muted)', marginTop: 2 }}>
                        {r.orders} order{r.orders === 1 ? '' : 's'}
                        {r.earliestDelivery && <> · due {dayjs(r.earliestDelivery).format('D MMM')}</>}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', flex: 'none' }}>
                      <div style={{ font: '700 14px monospace' }}>{fmtNum(r.pieces)} pcs</div>
                      <div style={{ marginTop: 2 }}><OnHandCell row={r} /></div>
                    </div>
                  </div>
                </div>
              ))}

              {!loading && view === 'order' && lines.map((r) => (
                <div key={r.lineId} style={{ border: '1px solid var(--border)', borderRadius: 12, padding: 12, background: 'var(--surface2)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                    <div style={{ minWidth: 0 }}>
                      <OrderLink orderId={r.orderId} />
                      <div style={{ font: '600 13px Outfit, sans-serif', marginTop: 3, ...clip }}>{shelfLabel(r)}</div>
                      <div style={{ marginTop: 5 }}>
                        <StatusChip status={r.status} heldFrom={r.heldFromStatus} darkMode={darkMode} />
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', flex: 'none' }}>
                      <div style={{ font: '700 14px monospace' }}>{fmtNum(r.pieces)} pcs</div>
                      <div style={{ font: '400 11.5px monospace', color: r.overdue ? STATUS.errorSoft : 'var(--dim)', marginTop: 2 }}>
                        {r.deliveryDate ? dayjs(r.deliveryDate).format('D MMM YY') : '—'}
                      </div>
                      <div style={{ marginTop: 2 }}><OnHandCell row={r} /></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Footer. Only the by-order view pages — by fabric is under a hundred
              rows and meant to be read whole. */}
          <div style={{ padding: '11px 18px', borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ font: '400 12px Outfit, sans-serif', color: 'var(--muted)' }}>
              {view === 'fabric'
                ? <>{shelves.length} fabric{shelves.length === 1 ? '' : 's'} wanted</>
                : <>{fmtNum(total)} order line{total === 1 ? '' : 's'}</>}
              {summary && <> · <span style={{ fontFamily: 'monospace', color: 'var(--text)' }}>{fmtNum(summary.pieces)}</span> pieces</>}
            </div>
            {view === 'order' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <RowsPicker
                  value={pageSize}
                  options={PAGE_SIZE_OPTIONS}
                  darkMode={darkMode}
                  onChange={(v) => { setPageSize(v); setPage(1); }}
                />
                <Pagination
                  page={page}
                  totalPages={lastPage}
                  onGo={(p) => setPage(p)}
                />
              </div>
            )}
          </div>
        </div>

        {/* One fabric, and the orders behind it. */}
        <PanelDrawer
          open={!!detail}
          onClose={() => setDetail(null)}
          darkMode={darkMode}
          width={isMobile ? '100%' : 460}
          title={detail ? shelfLabel(detail) : 'Fabric'}
          subtitle={detail ? `${fmtNum(detail.pieces)} pieces across ${detail.orders} order${detail.orders === 1 ? '' : 's'}` : undefined}
          bodyPadding={18}
        >
          {detail && (
            <>
              <div style={{ paddingBottom: 14, borderBottom: '1px solid var(--border)', font: '400 12.5px Outfit, sans-serif', color: 'var(--muted)' }}>
                {/* Said in words, because the two figures either side of it are
                    not comparable and a bare pair invites the subtraction. */}
                On hand <OnHandCell row={detail} />
                {detail.held ? ' — pieces and kilograms are not comparable; what a piece takes is not recorded anywhere.' : ''}
              </div>

              <div style={{ paddingTop: 12 }}>
                <div style={{ ...miniLabel, marginBottom: 8 }}>Orders waiting</div>
                {detailLoading && <LoadingRow />}
                {!detailLoading && detailRows.length === 0 && (
                  <div style={{ font: '400 12px Outfit, sans-serif', color: 'var(--dim)' }}>No lines to show.</div>
                )}
                {!detailLoading && detailRows.map((r) => (
                  <div key={r.lineId} style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--border)', alignItems: 'center' }}>
                    <div style={{ minWidth: 0 }}>
                      <OrderLink orderId={r.orderId} />
                      <div style={{ marginTop: 3 }}>
                        <StatusChip status={r.status} heldFrom={r.heldFromStatus} darkMode={darkMode} />
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', flex: 'none' }}>
                      <div style={{ font: '700 12.5px monospace' }}>{fmtNum(r.pieces)} pcs</div>
                      <div style={{ font: '400 11px monospace', color: r.overdue ? STATUS.errorSoft : 'var(--dim)' }}>
                        {r.deliveryDate ? dayjs(r.deliveryDate).format('D MMM YY') : '—'}
                      </div>
                    </div>
                  </div>
                ))}
                {detailRows.length >= 100 && (
                  <div style={{ padding: '8px 0 0', font: '400 11.5px Outfit, sans-serif', color: 'var(--dim)' }}>
                    First 100 lines. The By order view pages through the rest.
                  </div>
                )}
              </div>
            </>
          )}
        </PanelDrawer>
      </ConfigProvider>
    </div>
  );
}

export default FabricRequests;
